<template>
  <li class="col-span-1">
    <div
      class="group w-full h-full flex flex-col bg-white rounded-xl border border-untitled-gray-200 shadow-sm hover:shadow-md hover:border-untitled-gray-300 transition-all duration-200 overflow-hidden"
    >
      <!-- Thumbnail -->
      <div
        class="relative w-full aspect-video cursor-pointer overflow-hidden bg-untitled-gray-100"
        @click="handleClickToUnits"
      >
        <img
          v-lazy="{
            src: course.thumbnail,
            error:
              'https://lms-v2-staging.s3-us-west-2.amazonaws.com/modules/default-thumbnail.jpg',
            loading:
              'https://via.placeholder.com/800x450?text=Thumbnail%20Loading...',
          }"
          class="object-cover object-center w-full h-full transition-transform duration-300 group-hover:scale-105"
        />
        <div
          class="absolute inset-0 flex justify-center items-center bg-untitled-gray-900/0 group-hover:bg-untitled-gray-900/30 transition-colors duration-200"
        >
          <PlayIcon
            v-if="course.completed_percentage !== 100"
            class="w-12 h-12 text-white opacity-0 group-hover:opacity-90 transition-opacity duration-200"
          />
          <CheckCircleIcon v-else class="w-12 h-12 text-white opacity-80" />
        </div>

        <!-- Top-left badge -->
        <div
          v-if="course.assigned_course && !featured"
          class="absolute top-2 left-2 bg-untitled-gray-800/90 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full"
        >
          Assigned to you
        </div>
        <div
          v-else-if="featured || isNew"
          class="absolute top-2 left-2 bg-untitled-gray-800/90 text-white text-[11px] font-semibold uppercase px-2.5 py-1 rounded-full"
        >
          Course
        </div>

        <!-- Top-right badge -->
        <div
          v-if="course.completed_percentage === 100 && !featured && !isNew"
          class="absolute top-2 right-2 flex items-center gap-1 font-semibold text-green-700 text-[11px] bg-green-100 px-2.5 py-1 rounded-full"
        >
          Completed
        </div>
        <div
          v-else-if="featured"
          class="absolute top-2 right-2 flex items-center gap-1 font-bold uppercase text-red-600 text-[11px] bg-red-100 px-2.5 py-1 rounded-full"
        >
          <FireIcon class="w-3.5 h-3.5" />
          Featured
        </div>
        <div
          v-else-if="isNew"
          class="absolute top-2 right-2 flex items-center gap-1 font-bold uppercase text-green-700 text-[11px] bg-green-100 px-2.5 py-1 rounded-full"
        >
          <StarIcon class="w-3.5 h-3.5" />
          New
        </div>
      </div>

      <!-- Body -->
      <div class="flex flex-col gap-3 p-4 flex-1">
        <el-tooltip
          :content="wrapTooltipContent(course.name)"
          effect="dark"
          :raw-content="true"
          placement="top-start"
        >
          <h1
            class="text-base font-semibold text-untitled-gray-800 line-clamp-1 cursor-pointer"
            @click="handleClickToUnits"
          >
            {{ course.name }}
          </h1>
        </el-tooltip>

        <!-- Progress -->
        <div class="flex flex-col gap-1.5" v-if="showProgress">
          <div v-if="loadingNewContentsProgress" class="flex flex-col gap-2">
            <div class="h-2 bg-untitled-gray-100 rounded-full animate-pulse" />
            <div class="h-3 w-20 bg-untitled-gray-100 rounded animate-pulse" />
          </div>
          <template v-else>
            <div class="flex items-baseline gap-1">
              <span class="text-sm font-bold text-emerald-600"
                >{{ course.completed_percentage || 0 }}%</span
              >
              <span class="text-sm text-untitled-gray-500">Completed</span>
            </div>
            <div
              class="w-full h-2 rounded-full bg-untitled-gray-100 overflow-hidden"
            >
              <div
                class="h-full rounded-full bg-app-primary transition-all duration-300"
                :style="{ width: `${course.completed_percentage || 0}%` }"
              />
            </div>
          </template>
        </div>

        <!-- Meta -->
        <div
          class="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-untitled-gray-500"
        >
          <div class="flex items-center gap-1">
            <ClockIcon class="w-4 h-4 text-untitled-gray-400" />
            <span class="font-medium">{{
              course.course_duration ?? course.duration
            }}</span>
          </div>
          <router-link
            v-if="course.unit_display || course.link"
            class="flex items-center gap-1 hover:text-untitled-gray-700"
            :to="course.link ?? '/units/' + course.unit_display.id"
          >
            <VideoCameraIcon class="w-4 h-4 text-untitled-gray-400" />
            {{ course.course_details.units_video_count }} Videos
          </router-link>
          <router-link
            v-if="course.course_details.units_text_count"
            class="flex items-center gap-1 hover:text-untitled-gray-700"
            :to="course.link ?? '/units/' + course.unit_display.id"
          >
            <DocumentTextIcon class="w-4 h-4 text-untitled-gray-400" />
            {{ course.course_details.units_text_count }} Text Units
          </router-link>
          <el-tooltip
            v-if="course.assigned_course && course.assigned_course.due_date"
            effect="dark"
            content="Expected date and time of completion of the course"
          >
            <div class="flex items-center gap-1">
              <CalendarIcon class="w-4 h-4 text-untitled-gray-400" />
              {{ formatDate(course.assigned_course.due_date, "MM/DD/YYYY") }}
            </div>
          </el-tooltip>
        </div>

        <!-- Categories & published date -->
        <div
          v-if="course.categories?.length || course.published_at"
          class="flex flex-col gap-1.5 pt-2 border-t border-untitled-gray-100"
        >
          <V4CourseCategories
            :categories="course.categories"
            :numbers-to-show="3"
          />
          <span
            v-if="course.published_at"
            class="text-xs text-untitled-gray-400"
          >
            Published {{ formatDate(course.published_at, "MM/DD/YYYY") }}
          </span>
        </div>

        <!-- Actions -->
        <div class="mt-auto pt-1">
          <template v-if="!course.is_enrolled && hasIsEnrollProperty">
            <ViewCourse
              v-model="showEnrollCourse"
              :course="course"
              :course-id="course.id"
              class="w-full"
              @onEnrolledCourse="handleClickEnrollNow"
            />
          </template>
          <div v-else class="flex flex-row flex-wrap gap-2">
            <button
              type="button"
              @click="handleClickOpenUnit"
              class="flex-1 min-w-36 inline-flex justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-white bg-app-primary hover:bg-app-secondary transition-colors truncate cursor-pointer"
              :title="
                course.completed_percentage >= 100
                  ? 'Retake Course'
                  : course.started || course.completed_percentage > 0
                  ? 'Continue Learning'
                  : 'Start Learning'
              "
            >
              <CursorClickIcon class="w-4 h-4" />
              <span class="truncate">
                {{
                  course.completed_percentage >= 100
                    ? "Retake Course"
                    : course.started || course.completed_percentage > 0
                    ? "Continue Learning"
                    : "Start Learning"
                }}
              </span>
            </button>
            <AssignToUser
              v-if="course.can_assign_contents && !featured"
              :course-id="course.id"
              v-slot="{ clickAssign }"
              :title="`Assign ${course.name} Course`"
              label="Assign Course"
            >
              <button
                type="button"
                @click="clickAssign"
                v-has-restrict-learner
                class="flex-1 min-w-36 inline-flex justify-center items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-untitled-gray-700 bg-untitled-gray-100 hover:bg-untitled-gray-200 transition-colors truncate cursor-pointer"
              >
                <LinkIcon class="w-4 h-4" />
                Assign Course
              </button>
            </AssignToUser>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script setup>
import AssignToUser from "@/components/utilities/AssignToUser.vue";
import ViewCourse from "@/components/utilities/ViewCourse.vue";
import {
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  PlayIcon,
  VideoCameraIcon,
  CursorClickIcon,
  CheckCircleIcon,
  FireIcon,
  StarIcon,
  LinkIcon,
} from "@heroicons/vue/solid";
import { dayjs, ElTooltip } from "element-plus";
import { ref } from "vue";
import { computed } from "vue";
import { defineProps } from "vue";
import { useRouter } from "vue-router";
import { wrapTooltipContent } from "@/composable/wrapTooltipContent";
import V4CourseCategories from "./V4CourseCategories.vue";

const props = defineProps({
  course: {
    type: Object,
    required: true,
  },
  hasIsEnrollProperty: {
    type: Boolean,
    default: false,
  },
  featured: {
    type: Boolean,
  },
  isAllAvailableContent: {
    type: Boolean,
    default: false,
  },
  isNew: {
    type: Boolean,
  },
  showProgress: {
    type: Boolean,
    default: true,
  },
  loadingNewContentsProgress: {
    type: Boolean,
    default: false,
  },
});

const router = useRouter();

const course = computed(() => props.course);
const hasIsEnrollProperty = computed(() => props.hasIsEnrollProperty);
const featured = computed(() => props.featured);
const isNew = computed(() => props.isNew);

const showEnrollCourse = ref(false);

const handleClickToUnits = () => {
  if (course.value && hasIsEnrollProperty.value && !course.value.is_enrolled) {
    showEnrollCourse.value = true;
    return;
  }

  if (course.value.link) {
    router.push(course.value.link);

    return;
  }

  if (!props.course.unit_display) {
    return;
  }

  router.push(`/units/${props.course.unit_display.id}`);
};

const formatDate = (value, format) => {
  return dayjs(value).format(format);
};

const handleClickEnrollNow = () => {};

const handleClickOpenUnit = () => {
  if (course.value.link) {
    router.push(course.value.link);

    return;
  }

  if (!course.value.unit_display) {
    return;
  }

  router.push(`/units/${course.value.unit_display.id}`);
};
</script>

<style lang="scss" scoped></style>
